import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    // Fetch cart to get accurate count excluding add-on items
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Count only parent items (exclude items with parent_id)
      const itemCount = cart.items.filter(item => !item.parent).length;
      const comingFromProductForm = event.detail.data?.source === 'product-form-component';

      this.renderCartBubble(itemCount, comingFromProductForm);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
      // Fallback to event data if fetch fails
      const itemCount = event.detail.data?.itemCount ?? 0;
      const comingFromProductForm = event.detail.data?.source === 'product-form-component';
      this.renderCartBubble(itemCount, comingFromProductForm);
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // Always set the absolute count (not relative) since we fetch cart.js for accurate count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = async () => {
    // Fetch cart to ensure accurate count excluding add-on items
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Count only parent items (exclude items with parent_id)
      const itemCount = cart.items.filter(item => !item.parent).length;
      const visibleCount = parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);

      if (itemCount !== visibleCount) {
        this.renderCartBubble(itemCount, false, false);
      }
    } catch (error) {
      console.error('Failed to verify cart count:', error);
      // Fallback to session storage check
      const sessionStorageCount = sessionStorage.getItem('cart-count');
      const visibleCount = this.refs.cartBubbleCount.textContent;

      if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

      try {
        const { value, timestamp } = JSON.parse(sessionStorageCount);

        if (Date.now() - timestamp < 10000) {
          const count = parseInt(value, 10);

          if (count >= 0) {
            this.renderCartBubble(count, false, false);
          }
        }
      } catch (_) {
        // no-op
      }
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
